import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Modal } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { SVG_COMPONENTS } from '@/assets/admin';

interface MonthYearPickerErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface MonthYearPickerErrorBoundaryProps {
  children: React.ReactNode;
  onClose?: () => void;
  visible?: boolean;
}

export class MonthYearPickerErrorBoundary extends React.Component<
  MonthYearPickerErrorBoundaryProps,
  MonthYearPickerErrorBoundaryState
> {
  constructor(props: MonthYearPickerErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): MonthYearPickerErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('MonthYearPicker Error Boundary caught an error:', error, errorInfo);
  }

  handleClose = () => {
    this.setState({ hasError: false, error: undefined });
    this.props.onClose?.();
  };

  render() {
    if (this.state.hasError && this.props.visible) {
      return (
        <Modal
          visible={true}
          transparent
          animationType="fade"
          onRequestClose={this.handleClose}
        >
          <View style={styles.overlay}>
            <View style={styles.errorContainer}>
              <SVG_COMPONENTS.X_CIRCLE width={32} height={32} color={ADMIN_COLORS.ERROR} />
              <Text style={styles.errorTitle}>Calendar Error</Text>
              <Text style={styles.errorMessage}>
                Unable to load the month/year picker. Please try again.
              </Text>
              <TouchableOpacity style={styles.closeButton} onPress={this.handleClose}>
                <Text style={styles.closeButtonText}>Close</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Modal>
      );
    }

    return this.props.children;
  }
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 24,
    alignItems: 'center',
    maxWidth: 300,
  },
  errorTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.ERROR,
    marginTop: 12,
    marginBottom: 8,
  },
  errorMessage: {
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 20,
  },
  closeButton: {
    backgroundColor: ADMIN_COLORS.ERROR,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
  },
  closeButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '500',
  },
});
